import React, { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import { Plus, Trash, PencilSimple, X, ShieldCheck, ShieldWarning, Eye, Users, UserCircle, Truck } from '@phosphor-icons/react';
import { useConfirm } from '@/components/ConfirmDialog';
import VerkopersPanel from '@/components/VerkopersPanel';

// Granulaire machtigingen — zelfde keys & presets als VerkopersPanel
const MACHT_LABELS = [
  { key: 'artikelen_bekijken', label: 'Artikelen bekijken', hint: 'Kan artikelenlijst + detail inzien', icon: '📄' },
  { key: 'artikelen_aanmaken', label: 'Artikelen aanmaken', hint: 'Kan nieuwe artikels toevoegen', icon: '➕' },
  { key: 'artikelen_bewerken', label: 'Artikelen bewerken', hint: 'Kan bestaande artikels wijzigen', icon: '✏️' },
  { key: 'artikelen_verwijderen', label: 'Artikelen verwijderen', hint: 'Kan artikels in de prullenbak plaatsen', icon: '🗑️' },
  { key: 'prijs_wijzigen', label: 'Prijzen aanpassen', hint: 'Mag verkoopprijzen manueel wijzigen (bulk-wizard + per lijn)', icon: '💰' },
  { key: 'bestelbonnen', label: 'Bestellingen bekijken', hint: 'Kan alle bestelbonnen inzien', icon: '📦' },
  { key: 'bestellingen_aanmaken', label: 'Bestellingen aanmaken', hint: 'Kan nieuwe bestelbonnen opmaken', icon: '🛒' },
  { key: 'rapporten_bekijken', label: 'Rapporten bekijken', hint: 'Dashboard-grafieken, BTW-rapporten, commissies', icon: '📊' },
  { key: 'exporteren', label: 'Exporteren (Excel/CSV/CODA)', hint: 'Boekhoud-export, CODA-download, artikels CSV', icon: '📤' },
  { key: 'klanten_aanmaken', label: 'Klanten aanmaken/bewerken', hint: 'Anders: enkel selecteren uit bestaande klantenlijst', icon: '👥' },
  { key: 'kortingen_geven', label: 'Kortingen toekennen', hint: 'Activeert korting-veld op bestelbon-lijnen', icon: '🏷️' },
  { key: 'gebruikers_beheren', label: 'Gebruikers beheren', hint: 'Gebruikers aanmaken, paswoorden resetten, rollen toekennen', icon: '👤' },
  { key: 'instellingen_wijzigen', label: 'Instellingen wijzigen', hint: 'Bedrijfsgegevens, BTW, PDF-layout, automatiseringen', icon: '⚙️' },
  { key: 'machtigingen_toewijzen', label: 'Machtigingen toewijzen', hint: 'Kan zelf de rechten van andere gebruikers wijzigen', icon: '🔐' },
];

// Snelkeuze-presets — identiek aan VerkopersPanel
const SNELKEUZE_PRESETS = {
  'beheerder': {
    label: 'Beheerder', kleur: '#2563EB',
    macht: Object.fromEntries(MACHT_LABELS.map(m => [m.key, true])),
  },
  'verkoper': {
    label: 'Verkoper', kleur: '#059669',
    macht: {
      artikelen_bekijken: true, artikelen_aanmaken: true, artikelen_bewerken: true, artikelen_verwijderen: false,
      prijs_wijzigen: true, bestelbonnen: true, bestellingen_aanmaken: true, rapporten_bekijken: true,
      exporteren: true, klanten_aanmaken: true, kortingen_geven: true,
      gebruikers_beheren: false, instellingen_wijzigen: false, machtigingen_toewijzen: false,
    },
  },
  'medewerker': {
    label: 'Medewerker', kleur: '#D97706',
    macht: {
      artikelen_bekijken: true, artikelen_aanmaken: true, artikelen_bewerken: false, artikelen_verwijderen: false,
      prijs_wijzigen: false, bestelbonnen: true, bestellingen_aanmaken: true, rapporten_bekijken: false,
      exporteren: false, klanten_aanmaken: true, kortingen_geven: true,
      gebruikers_beheren: false, instellingen_wijzigen: false, machtigingen_toewijzen: false,
    },
  },
  'gast': {
    label: 'Gast', kleur: '#6B7280',
    macht: Object.fromEntries(MACHT_LABELS.map(m => [m.key, false])),
  },
};

// Apple Watch Ultra-oranje iOS-toggle (identiek aan VerkopersPanel)
function IOSSwitch({ checked, onChange, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className="relative inline-flex shrink-0 items-center transition-all duration-200"
      style={{
        width: 52, height: 32, borderRadius: 999,
        backgroundColor: checked ? '#FF6B0D' : '#E5E7EB',
        boxShadow: checked
          ? '0 0 0 1px rgba(255,107,13,0.25), 0 0 12px rgba(255,107,13,0.45), inset 0 1px 2px rgba(0,0,0,0.08)'
          : 'inset 0 0 0 1px rgba(0,0,0,0.05)',
        cursor: 'pointer', border: 0, padding: 0,
      }}
      data-testid={testid}
    >
      <span style={{
        position: 'absolute', left: checked ? 23 : 3, top: 3,
        width: 26, height: 26, borderRadius: '50%', background: '#FFFFFF',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)',
        transition: 'left 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
      }} />
    </button>
  );
}


const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Volledige toegang', color: '#DC2626' },
  { value: 'gebruiker', label: 'Gebruiker', desc: 'Lezen & schrijven', color: '#002FA7' },
  { value: 'alleen-lezen', label: 'Alleen lezen', desc: 'Alleen bekijken', color: '#6B7280' },
];

const RoleIcon = ({ role }) => {
  if (role === 'admin') return <ShieldCheck size={14} className="text-red-600" />;
  if (role === 'alleen-lezen') return <Eye size={14} className="text-gray-500" />;
  return <ShieldWarning size={14} className="text-[#002FA7]" />;
};

export default function GebruikersPage() {
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('gebruikers'); // 'gebruikers' | 'verkopers'
  const [verkopersCount, setVerkopersCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'gebruiker', machtigingen: {} });
  const [msg, setMsg] = useState('');
  const [matrixOpen, setMatrixOpen] = useState(true);
  const [currentUserId, setCurrentUserId] = useState('');
  // 🔓 Rate-limit toggle (debug-modus tegen 15-min lockout tijdens login-issues)
  const [rateLimit, setRateLimit] = useState({ uitgeschakeld: false, uit_tot: '' });
  const [rateLimitBusy, setRateLimitBusy] = useState(false);

  const load = () => API.get('/gebruikers').then(r => setUsers(r.data)).catch(() => {});
  const loadRateLimit = () => API.get('/admin/rate-limit-status').then(r => setRateLimit(r.data || {})).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    API.get('/auth/me').then(r => setCurrentUserId(r.data?._id || '')).catch(() => {});
    // Tel verkopers voor de tab-badge
    API.get('/verkopers').then(r => setVerkopersCount((r.data || []).length)).catch(() => setVerkopersCount(0));
    loadRateLimit();
    // Tab uit URL query (?tab=verkopers) zodat we kunnen deeplinken
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get('tab');
      if (t === 'verkopers') setActiveTab('verkopers');
    } catch { /* noop */ }
  }, []);

  const toggleRateLimit = async (uit, minuten) => {
    setRateLimitBusy(true);
    try {
      await API.post('/admin/rate-limit-toggle', { uitgeschakeld: uit, minuten_uit: minuten || 0 });
      await loadRateLimit();
    } catch (e) {
      alert(e.response?.data?.detail || 'Rate-limit toggle mislukt');
    } finally {
      setRateLimitBusy(false);
    }
  };

  // Matrix = MACHT_LABELS (zelfde als Verkopers) — check de user.machtigingen dict
  const alleMachtigingen = useMemo(() => MACHT_LABELS.map(m => ({ ...m, categorie: 'machtigingen' })), []);

  const heeftToegang = (user, macht) => !!(user.machtigingen || {})[macht.key];

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const resetForm = () => { setForm({ email: '', name: '', password: '', role: 'gebruiker', machtigingen: {} }); setEditId(null); setShowForm(false); };

  const save = async () => {
    try {
      if (editId) {
        const payload = { role: form.role, name: form.name, machtigingen: form.machtigingen };
        if (form.password) payload.password = form.password;
        await API.put(`/gebruikers/${editId}`, payload);
        setMsg('Gebruiker bijgewerkt');
      } else {
        if (!form.email) { alert('E-mail is verplicht'); return; }
        await API.post('/gebruikers', { ...form, password: form.password || 'welkom123' });
        setMsg('Gebruiker aangemaakt');
      }
      resetForm();
      load();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Fout bij opslaan');
    }
  };

  const startEdit = (u) => {
    setForm({ email: u.email, name: u.name || '', password: '', role: u.role || 'gebruiker', machtigingen: u.machtigingen || {} });
    setEditId(u._id);
    setShowForm(true);
  };

  // Toggle één machtiging
  const toggleMacht = (key, value) => setForm(p => ({ ...p, machtigingen: { ...(p.machtigingen || {}), [key]: value } }));

  // Snelkeuze-preset toepassen
  const pasSnelkeuzeToe = (preset_key) => {
    const preset = SNELKEUZE_PRESETS[preset_key];
    if (!preset) return;
    setForm(p => ({ ...p, machtigingen: { ...preset.macht } }));
  };

  // Detecteer actieve preset (exact overeenkomst)
  const actievePreset = useMemo(() => {
    const m = form.machtigingen || {};
    for (const [key, preset] of Object.entries(SNELKEUZE_PRESETS)) {
      const exact = MACHT_LABELS.every(mach => Boolean(m[mach.key]) === Boolean(preset.macht[mach.key]));
      if (exact) return key;
    }
    return null;
  }, [form.machtigingen]);

  const del = async (uid) => {
    const u = users.find(x => x.id === uid);
    const naam = u ? (u.name || u.email || 'deze gebruiker') : 'deze gebruiker';
    const ok = await confirm({
      title: 'Gebruiker verwijderen',
      subtitle: 'Definitief verwijderen — niet omkeerbaar.',
      message: `"${naam}" wordt verwijderd. De gebruiker kan niet meer inloggen. Documenten en historiek blijven bewaard.`,
      tone: 'danger',
      confirmText: 'Verwijderen',
    });
    if (!ok) return;
    try {
      await API.delete(`/gebruikers/${uid}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fout bij verwijderen');
    }
  };

  const roleInfo = (r) => ROLES.find(x => x.value === r) || ROLES[1];

  return (
    <div data-testid="gebruikers-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="page-heading mb-0">Gebruikers & Rechten</h1>
        {activeTab === 'gebruikers' && (
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-user-btn">
            <Plus size={14} weight="bold" /> Nieuwe gebruiker
          </button>
        )}
      </div>

      {/* Tab-bar — iOS-stijl met badges */}
      <div className="mb-5 flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit" data-testid="gebruikers-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'gebruikers'}
          onClick={() => { setActiveTab('gebruikers'); try { const url = new URL(window.location.href); url.searchParams.delete('tab'); window.history.replaceState({}, '', url); } catch { /* noop */ } }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all"
          style={{
            background: activeTab === 'gebruikers' ? '#FFFFFF' : 'transparent',
            color: activeTab === 'gebruikers' ? '#002FA7' : '#6B7280',
            boxShadow: activeTab === 'gebruikers' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          }}
          data-testid="tab-gebruikers"
        >
          <Users size={15} weight={activeTab === 'gebruikers' ? 'bold' : 'regular'} />
          Gebruikers
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: activeTab === 'gebruikers' ? '#E8EEFF' : '#E5E7EB', color: activeTab === 'gebruikers' ? '#002FA7' : '#6B7280' }}>
            {users.length}
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'verkopers'}
          onClick={() => { setActiveTab('verkopers'); try { const url = new URL(window.location.href); url.searchParams.set('tab', 'verkopers'); window.history.replaceState({}, '', url); } catch { /* noop */ } }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all"
          style={{
            background: activeTab === 'verkopers' ? '#FFFFFF' : 'transparent',
            color: activeTab === 'verkopers' ? '#059669' : '#6B7280',
            boxShadow: activeTab === 'verkopers' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          }}
          data-testid="tab-verkopers"
        >
          <Truck size={15} weight={activeTab === 'verkopers' ? 'bold' : 'regular'} />
          Verkopers
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: activeTab === 'verkopers' ? '#D1FAE5' : '#E5E7EB', color: activeTab === 'verkopers' ? '#059669' : '#6B7280' }}>
            {verkopersCount}
          </span>
        </button>
      </div>

      {activeTab === 'verkopers' ? (
        <div data-testid="verkopers-tab-content">
          {/* 🔓 Rate-limit toggle paneel — debugging anti-lockout */}
          <div className="mb-4 bg-white border border-gray-200 rounded-2xl p-4" data-testid="rate-limit-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, background: rateLimit.uitgeschakeld ? '#F59E0B' : '#10B981' }}>
                    <ShieldCheck size={16} weight="bold" color="#fff" />
                  </div>
                  <div className="font-bold text-gray-900 text-sm">Anti-brute-force lockout</div>
                  {rateLimit.uitgeschakeld ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">DEBUG-MODUS — UIT</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300">BEVEILIGD — AAN</span>
                  )}
                </div>
                <div className="text-[11px] text-gray-600 leading-snug">
                  Standaard: na <strong>5 foute pogingen</strong> wordt een account 15 minuten vergrendeld.
                  {rateLimit.uitgeschakeld && rateLimit.uit_tot && (
                    <span className="block mt-1 text-amber-700 font-bold">
                      ⏰ Tijdelijk uitgeschakeld tot: {new Date(rateLimit.uit_tot).toLocaleString('nl-BE')} — auto-revert daarna.
                    </span>
                  )}
                  {!rateLimit.uitgeschakeld && (
                    <span className="block mt-0.5 text-gray-500 italic">Schakel tijdelijk uit als verkopers vastlopen op "Te veel pogingen" tijdens debug/setup.</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!rateLimit.uitgeschakeld ? (
                  <>
                    <button
                      className="btn btn-sm bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                      onClick={() => toggleRateLimit(true, 30)}
                      disabled={rateLimitBusy}
                      data-testid="rate-limit-uit-30min"
                      title="Schakel uit voor 30 minuten — auto-revert"
                    >
                      ⏱ 30 min uit
                    </button>
                    <button
                      className="btn btn-sm bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                      onClick={() => { if (window.confirm('Lockout PERMANENT uitschakelen? Verkopers kunnen dan ongelimiteerd inloggen tot je het weer aanzet.')) toggleRateLimit(true, 0); }}
                      disabled={rateLimitBusy}
                      data-testid="rate-limit-uit-permanent"
                      title="Schakel uit zonder einddatum — opnieuw aanzetten via deze knop"
                    >
                      🔓 Permanent uit
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-primary btn-sm font-bold"
                    onClick={() => toggleRateLimit(false, 0)}
                    disabled={rateLimitBusy}
                    data-testid="rate-limit-aan"
                  >
                    🛡 Lockout opnieuw activeren
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Uitleg-banner voor verkopers */}
          <div className="mb-5 bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-4 text-xs text-gray-700 space-y-1.5" style={{ border: '1px solid rgba(5,150,105,0.18)' }}>
            <div className="font-bold text-emerald-800 mb-1 inline-flex items-center gap-1.5">
              <Truck size={14} weight="bold" /> Verkopers met email-login — voor verkoop onderweg
            </div>
            <div>🚚 <strong>Verkopers</strong> loggen in op <code className="px-1.5 py-0.5 bg-emerald-100 text-emerald-900 rounded font-mono text-[11px]">/login</code> met hun e-mailadres + wachtwoord (zelfde pagina als jij).</div>
            <div>📱 Na login zien ze direct <strong>"Verkoop onderweg"</strong> in het zijmenu — POS-vorm voor klantbezoek op telefoon/tablet.</div>
            <div>📧 Vink <strong>"Stuur welkomstmail"</strong> aan bij aanmaken — verkoper krijgt automatisch e-mail met inloggegevens + 1-klik login-link.</div>
            <div>🔢 Optioneel: geef ook een <strong>PIN-code</strong> voor snelle inlog op een gedeeld toestel (winkel-tablet/iPad).</div>
          </div>
          <VerkopersPanel />
        </div>
      ) : (
        <>
      {/* Uitleg banner — iOS stijl */}
      <div className="mb-5 bg-gradient-to-br from-[#F0F4FF] to-white rounded-2xl p-4 text-xs text-gray-700 space-y-1.5" style={{ border: '1px solid rgba(0,47,167,0.15)' }}>
        <div className="font-bold text-[#002FA7] mb-1">3 rollen — kies wat elke collega mag:</div>
        <div>🛡️ <strong>Admin</strong> — volledige toegang, inclusief verwijderen, prullenbak, gebruikersbeheer, instellingen.</div>
        <div>🔑 <strong>Gebruiker</strong> — documenten/klanten/artikels aanmaken en bewerken. Geen toegang tot gebruikersbeheer of admin-only instellingen.</div>
        <div>👁️ <strong>Alleen lezen</strong> — enkel bekijken, niets wijzigen. Ideaal voor accountants/stagiairs.</div>
        <div className="pt-2 text-[11px] border-t border-[#002FA7]/10 mt-2 text-gray-600">
          💡 Voor <strong>granulaire</strong> controle (bv. "verberg bestelbonnen voor alle gebruikers behalve admin") ga naar <a href="/instellingen?tab=functies" className="text-[#002FA7] font-bold underline hover:opacity-80">Instellingen → Functies (On/Off)</a>.
        </div>
      </div>

      {msg && <div className="mb-4 p-3 border border-green-200 bg-green-50 text-green-800 text-sm rounded-lg" data-testid="user-msg">{msg}</div>}

      {/* Rollen-presets — iOS stijl chips zoals Verkopers-panel */}
      <div className="mb-5 flex flex-wrap gap-2" data-testid="rol-presets">
        {ROLES.map(r => (
          <div key={r.value} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${r.color}15`, border: `1px solid ${r.color}30` }}>
            <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
            <span className="text-[12px] font-bold" style={{ color: r.color }}>{r.label}</span>
            <span className="text-[10px] text-gray-500">— {r.desc}</span>
          </div>
        ))}
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="mb-5 bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between" style={{ background: '#F5F5F7' }}>
            <span className="text-[13px] font-bold text-gray-900">{editId ? 'Gebruiker bewerken' : 'Nieuwe gebruiker'}</span>
            <button className="text-gray-400 hover:text-gray-700" onClick={resetForm}><X size={16} /></button>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">E-mail</label>
                <input className="field-input" value={form.email} onChange={u('email')} placeholder="gebruiker@email.be" disabled={!!editId} data-testid="user-email-input" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Naam</label>
                <input className="field-input" value={form.name} onChange={u('name')} placeholder="Volledige naam" data-testid="user-name-input" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">{editId ? 'Nieuw wachtwoord (leeg = behouden)' : 'Wachtwoord'}</label>
                <input type="password" className="field-input" value={form.password} onChange={u('password')} placeholder={editId ? '••••••••' : 'welkom123'} data-testid="user-password-input" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Rol (basis)</label>
                <div className="flex flex-wrap gap-1.5" data-testid="rol-pills">
                  {ROLES.map(r => (
                    <button
                      type="button" key={r.value}
                      onClick={() => setForm(p => ({ ...p, role: r.value }))}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all"
                      style={{
                        background: form.role === r.value ? r.color : `${r.color}15`,
                        color: form.role === r.value ? '#fff' : r.color,
                        border: `1px solid ${form.role === r.value ? r.color : `${r.color}30`}`,
                        boxShadow: form.role === r.value ? `0 0 10px ${r.color}40` : 'none',
                      }}
                      data-testid={`rol-pill-${r.value}`}
                    >{r.label}</button>
                  ))}
                </div>
                <div className="text-[10px] text-gray-500 mt-1.5">{ROLES.find(r => r.value === form.role)?.desc}</div>
              </div>
            </div>

            {/* Machtigingen-editor — identiek aan VerkopersPanel (Apple Watch Ultra-oranje toggles) */}
            <div className="mt-6 pt-5 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[13px] font-bold text-gray-900">Machtigingen</div>
                  <div className="text-[11px] text-gray-500">Kies exact wat deze gebruiker mag (overschrijft rol-defaults)</div>
                </div>
              </div>

              {/* Snelkeuze-pills */}
              <div className="mb-4" data-testid="snelkeuze-pills">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Snelkeuze</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(SNELKEUZE_PRESETS).map(([key, preset]) => {
                    const actief = actievePreset === key;
                    return (
                      <button
                        type="button" key={key}
                        onClick={() => pasSnelkeuzeToe(key)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all"
                        style={{
                          background: actief ? preset.kleur : `${preset.kleur}15`,
                          color: actief ? '#fff' : preset.kleur,
                          border: `1px solid ${actief ? preset.kleur : `${preset.kleur}30`}`,
                          boxShadow: actief ? `0 0 10px ${preset.kleur}40` : 'none',
                        }}
                        data-testid={`snelkeuze-${key}`}
                      >{preset.label}</button>
                    );
                  })}
                  {!actievePreset && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-200">CUSTOM</span>
                  )}
                </div>
              </div>

              {/* Machtigingen-lijst met iOS-toggles */}
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
                {MACHT_LABELS.map((m, idx) => {
                  const on = !!form.machtigingen?.[m.key];
                  return (
                    <div
                      key={m.key}
                      className="flex items-center gap-3 px-3 py-2.5"
                      style={{
                        background: on ? 'rgba(255,107,13,0.05)' : '#FFFFFF',
                        borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.06)',
                        transition: 'background-color 0.15s',
                      }}
                      data-testid={`gebr-macht-row-${m.key}`}
                    >
                      <div className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 10, background: on ? '#FF6B0D' : '#E5E7EB', fontSize: 18, transition: 'background-color 0.2s' }}>
                        {m.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-gray-900 leading-tight">{m.label}</div>
                        <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{m.hint}</div>
                      </div>
                      <IOSSwitch checked={on} onChange={v => toggleMacht(m.key, v)} testid={`gebr-toggle-${m.key}`} />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button className="btn btn-primary" onClick={save} data-testid="save-user-btn">
                {editId ? 'Bijwerken' : 'Aanmaken'}
              </button>
              <button className="btn btn-ghost" onClick={resetForm}>Annuleren</button>
            </div>
          </div>
        </div>
      )}

      {/* Gebruikers — iOS Settings-stijl lijst (zoals Verkopers) */}
      <div className="space-y-2" data-testid="gebruikers-lijst">
        {users.length === 0 ? (
          <div className="text-center text-gray-400 italic py-10 bg-white rounded-2xl" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>Geen gebruikers gevonden</div>
        ) : users.map(usr => {
          const ri = roleInfo(usr.role);
          const initials = (usr.name || usr.email || '?').split(/\s+|[@.]/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
          const isSelf = currentUserId === usr._id;
          return (
            <div
              key={usr._id}
              className="bg-white rounded-2xl overflow-hidden flex items-center gap-3 px-3 py-3 hover:shadow-sm transition-shadow"
              style={{ border: '1px solid rgba(0,0,0,0.06)' }}
              data-testid={`gebruiker-card-${usr._id}`}
            >
              {/* Avatar */}
              <div
                className="flex items-center justify-center text-white font-bold text-[14px] shrink-0"
                style={{ width: 44, height: 44, borderRadius: '50%', background: ri.color, boxShadow: '0 2px 4px rgba(0,0,0,0.12)' }}
              >
                {initials}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[14px] font-bold text-gray-900">{usr.name || usr.email.split('@')[0]}</span>
                  {isSelf && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">IK</span>}
                </div>
                <div className="text-[12px] text-gray-500 truncate">{usr.email}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Aangemaakt {(usr.created_at || '').split('T')[0]}</div>
              </div>
              {/* Rol-badge */}
              <span
                className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
                style={{ background: `${ri.color}15`, color: ri.color, border: `1px solid ${ri.color}30` }}
              >
                <RoleIcon role={usr.role} /> {ri.label}
              </span>
              {/* Acties */}
              <div className="flex items-center gap-1">
                <button
                  className="flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                  style={{ width: 36, height: 36 }}
                  onClick={() => startEdit(usr)}
                  title="Bewerken"
                  data-testid={`edit-user-${usr._id}`}
                >
                  <PencilSimple size={16} className="text-gray-600" />
                </button>
                <button
                  className="flex items-center justify-center hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ width: 36, height: 36 }}
                  onClick={() => del(usr._id)}
                  title={isSelf ? 'U kunt uzelf niet verwijderen' : 'Verwijderen'}
                  disabled={isSelf}
                  data-testid={`del-user-${usr._id}`}
                >
                  <Trash size={15} className="text-red-500" />
                </button>
              </div>
            </div>
          );
        })}
      </div>


      {/* Machtigingen-matrix voor gebruikers — zelfde iOS-stijl als Verkopers */}
      {users.length >= 1 && (
        <div className="mt-6" data-testid="gebruikers-machtigingen-matrix">
          <button
            type="button"
            onClick={() => setMatrixOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 transition-all"
            style={{
              background: '#FFFFFF',
              border: '1px solid rgba(0,0,0,0.06)',
              borderRadius: matrixOpen ? '16px 16px 0 0' : 16,
              boxShadow: matrixOpen ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
              cursor: 'pointer',
            }}
            data-testid="gebr-matrix-toggle"
          >
            <span className="inline-flex items-center gap-3">
              <span className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 10, background: '#002FA7', fontSize: 16, boxShadow: '0 0 10px rgba(0,47,167,0.25)' }}>
                <span style={{ filter: 'brightness(0) invert(1)' }}>🔐</span>
              </span>
              <span className="flex flex-col items-start">
                <span className="text-[14px] font-semibold text-gray-900 leading-tight">Machtigingen-matrix (gebruikers)</span>
                <span className="text-[11px] text-gray-500 leading-tight mt-0.5">Per-gebruiker granulaire rechten — {alleMachtigingen.length} machtigingen × {users.length} gebruikers</span>
              </span>
            </span>
            <span className="text-gray-400" style={{ fontSize: 18, transform: matrixOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>›</span>
          </button>
          {matrixOpen && (
            <div className="overflow-x-auto" style={{ background: '#F5F5F7', border: '1px solid rgba(0,0,0,0.06)', borderTop: 'none', borderRadius: '0 0 16px 16px' }}>
              <div style={{ padding: 8 }}>
                <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, background: '#FFFFFF', borderRadius: 12, overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: '#F5F5F7' }}>
                      <th className="text-left px-3 py-2 font-semibold text-gray-500 text-[11px] uppercase tracking-wider sticky left-0" style={{ background: '#F5F5F7', minWidth: 260, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>Machtiging</th>
                      {users.map(usr => {
                        const info = roleInfo(usr.role);
                        const initials = (usr.name || usr.email || '?').split(/\s+|[@.]/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                        return (
                          <th key={usr._id} className="px-3 py-2 whitespace-nowrap" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', minWidth: 130 }}>
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex items-center justify-center text-white font-bold text-[11px]" style={{ width: 32, height: 32, borderRadius: '50%', background: info.color, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }} title={usr.name || usr.email}>
                                {initials}
                              </div>
                              <span className="text-[12px] font-semibold text-gray-900 leading-tight" style={{ maxWidth: 120 }}>{usr.name || usr.email.split('@')[0]}</span>
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide" style={{ background: `${info.color}15`, color: info.color }}>{info.label}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {alleMachtigingen.map((m, idx) => (
                      <tr key={m.key} className="group hover:bg-blue-50/20" style={{ borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.04)' }}>
                        <td className="px-3 py-2 sticky left-0 bg-white group-hover:bg-blue-50/20">
                          <div className="flex items-center gap-2.5">
                            <div className="flex items-center justify-center shrink-0" style={{ width: 26, height: 26, borderRadius: 7, background: '#F5F5F7', fontSize: 13 }}>{m.icon}</div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-[12px] font-semibold text-gray-900 leading-tight truncate" style={{ maxWidth: 240 }}>{m.label}</span>
                              <span className="text-[10px] text-gray-500 leading-tight mt-0.5 truncate" style={{ maxWidth: 240 }}>{m.hint}</span>
                            </div>
                          </div>
                        </td>
                        {users.map(usr => {
                          const ok = heeftToegang(usr, m);
                          return (
                            <td key={usr._id} className="text-center px-3 py-2">
                              {ok ? (
                                <span className="inline-flex items-center justify-center" style={{ width: 22, height: 22, borderRadius: '50%', background: '#34C759', color: '#fff', fontSize: 13, fontWeight: 800, boxShadow: '0 1px 2px rgba(52,199,89,0.3)' }} title="Toegestaan">✓</span>
                              ) : (
                                <span className="inline-block" style={{ width: 22, height: 3, borderRadius: 2, background: '#D1D5DB' }} title="Geen toegang" />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center gap-4 px-3 py-2 text-[10px] text-gray-500">
                  <span className="inline-flex items-center gap-1.5"><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: '#34C759' }}></span>Toegestaan</span>
                  <span className="inline-flex items-center gap-1.5"><span style={{ display: 'inline-block', width: 12, height: 3, borderRadius: 2, background: '#D1D5DB' }}></span>Geen toegang</span>
                  <span className="ml-auto italic">Berekend op basis van rol × feature-configuratie (Instellingen → Functies)</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}
